
import React from "react";

// ========================================
// PRODUCT QUALIFICATION QUESTIONS
// ========================================

const ProductQualificationQuestions = ({
    data = [],
    onChange
}) => {

    const questions = Array.isArray(data)
        ? data
        : [];

    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const labelClass =
        "block text-sm font-medium text-gray-700 mb-2";

    const addButtonClass =
        "px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";

    // ========================================
    // ADD QUESTION
    // ========================================

    const addQuestion = () => {

        const nextOrder = questions.reduce(
            (max, question) =>
                Math.max(
                    max,
                    Number(question.order) || 0
                ),
            0
        ) + 1;

        onChange([
            ...questions,
            {
                question: "",
                purpose: "",
                condition: "",
                requiredForLead: false,
                order: nextOrder
            }
        ]);

    };

    // ========================================
    // UPDATE QUESTION
    // ========================================

    const updateQuestion = (
        index,
        field,
        value
    ) => {

        onChange(
            questions.map(
                (item, i) =>
                    i === index
                        ? {
                            ...item,
                            [field]: value
                        }
                        : item
            )
        );

    };

    // ========================================
    // REMOVE QUESTION
    // ========================================

    const removeQuestion = (index) => {

        const updatedQuestions = questions.filter(
            (_, i) => i !== index
        );

        onChange(
            updatedQuestions.map(
                (question, i) => ({
                    ...question,
                    order: i + 1
                })
            )
        );

    };

    // ========================================
    // MOVE QUESTION
    // ========================================

    const moveQuestion = (index, direction) => {

        const targetIndex = index + direction;

        if (
            targetIndex < 0 ||
            targetIndex >= questions.length
        ) {
            return;
        }

        const updatedQuestions = [...questions];

        [
            updatedQuestions[index],
            updatedQuestions[targetIndex]
        ] = [
            updatedQuestions[targetIndex],
            updatedQuestions[index]
        ];

        onChange(
            updatedQuestions.map(
                (question, i) => ({
                    ...question,
                    order: i + 1
                })
            )
        );

    };

    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-6">

            {/* HEADER */}

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b pb-4">

                <div>

                    <h3 className="text-base font-semibold text-gray-900">
                        Customer Qualification Questions
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                        Define the questions the AI agent should
                        ask to understand customer requirements,
                        assess product suitability and qualify
                        potential sales leads.
                    </p>

                </div>

                <button
                    type="button"
                    onClick={addQuestion}
                    className={addButtonClass}
                >
                    + Add Question
                </button>

            </div>

            {/* EMPTY STATE */}

            {questions.length === 0 && (

                <div className="py-12 text-center border border-dashed rounded-lg">

                    <p className="text-sm text-gray-500">
                        No qualification questions added yet.
                    </p>

                    <p className="text-xs text-gray-400 mt-2">
                        Add questions that help the AI agent
                        understand customer requirements.
                    </p>

                </div>

            )}

            {/* QUESTIONS */}

            {questions.map((item, index) => (

                <div
                    key={item._id || index}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                >

                    {/* QUESTION HEADER */}

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-gray-100 px-5 py-4">

                        <div>

                            <h4 className="font-semibold text-gray-900">
                                Question {index + 1}
                            </h4>

                            {item.requiredForLead && (

                                <p className="text-xs text-gray-600 mt-1">
                                    Required for lead qualification
                                </p>

                            )}

                        </div>

                        <div className="flex flex-wrap items-center gap-2">

                            <button
                                type="button"
                                onClick={() =>
                                    moveQuestion(index, -1)
                                }
                                disabled={index === 0}
                                className="px-3 py-2 border rounded text-sm bg-white disabled:opacity-40"
                            >
                                ↑ Up
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    moveQuestion(index, 1)
                                }
                                disabled={index === questions.length - 1}
                                className="px-3 py-2 border rounded text-sm bg-white disabled:opacity-40"
                            >
                                ↓ Down
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    removeQuestion(index)
                                }
                                className={removeButtonClass}
                            >
                                Remove
                            </button>

                        </div>

                    </div>

                    {/* QUESTION FIELDS */}

                    <div className="p-5 space-y-5">

                        {/* QUESTION */}

                        <div>

                            <label className={labelClass}>
                                Question *
                            </label>

                            <textarea
                                value={item.question || ""}
                                onChange={(event) =>
                                    updateQuestion(
                                        index,
                                        "question",
                                        event.target.value
                                    )
                                }
                                placeholder="e.g. What type of surface are you planning to waterproof?"
                                rows={3}
                                className={inputClass}
                            />

                        </div>

                        {/* PURPOSE */}

                        <div>

                            <label className={labelClass}>
                                Purpose
                            </label>

                            <textarea
                                value={item.purpose || ""}
                                onChange={(event) =>
                                    updateQuestion(
                                        index,
                                        "purpose",
                                        event.target.value
                                    )
                                }
                                placeholder="e.g. Identify the substrate to determine which products may be suitable."
                                rows={3}
                                className={inputClass}
                            />

                            <p className="text-xs text-gray-500 mt-2">
                                Explain why the AI agent needs
                                this information.
                            </p>

                        </div>

                        {/* CONDITION */}

                        <div>

                            <label className={labelClass}>
                                When Should This Question Be Asked?
                            </label>

                            <textarea
                                value={item.condition || ""}
                                onChange={(event) =>
                                    updateQuestion(
                                        index,
                                        "condition",
                                        event.target.value
                                    )
                                }
                                placeholder="e.g. Ask when the customer is seeking a waterproofing solution but has not identified the surface type."
                                rows={3}
                                className={inputClass}
                            />

                            <p className="text-xs text-gray-500 mt-2">
                                Describe the circumstances in
                                which the question is relevant.
                                Leave blank if generally applicable.
                            </p>

                        </div>

                        {/* REQUIRED FOR LEAD */}

                        <div className="border rounded-lg p-4 bg-gray-50">

                            <label className="flex items-start gap-3 cursor-pointer">

                                <input
                                    type="checkbox"
                                    checked={
                                        item.requiredForLead || false
                                    }
                                    onChange={(event) =>
                                        updateQuestion(
                                            index,
                                            "requiredForLead",
                                            event.target.checked
                                        )
                                    }
                                    className="mt-1 h-4 w-4"
                                />

                                <div>

                                    <span className="block text-sm font-medium text-gray-900">
                                        Required for Lead Qualification
                                    </span>

                                    <p className="text-xs text-gray-500 mt-1">
                                        Mark this when the customer's
                                        answer is necessary to qualify
                                        the enquiry before passing it
                                        to the sales team.
                                    </p>

                                </div>

                            </label>

                        </div>

                        {/* ORDER */}

                        <div className="max-w-xs">

                            <label className={labelClass}>
                                Question Order
                            </label>

                            <input
                                type="number"
                                min="0"
                                step="1"
                                value={item.order ?? 0}
                                onChange={(event) =>
                                    updateQuestion(
                                        index,
                                        "order",
                                        event.target.value === ""
                                            ? 0
                                            : Number(event.target.value)
                                    )
                                }
                                className={inputClass}
                            />

                            <p className="text-xs text-gray-500 mt-2">
                                Use the Up/Down controls to
                                change the displayed sequence.
                            </p>

                        </div>

                    </div>

                </div>

            ))}

            {/* GUIDANCE */}

            <div className="border-t pt-4">

                <p className="text-xs text-gray-500">
                    Qualification questions should guide the AI
                    agent naturally. The agent should use information
                    already provided by the customer, avoid asking
                    unnecessary or repeated questions, and ask
                    relevant questions based on the conversation.
                </p>

            </div>

        </div>

    );

};

export default ProductQualificationQuestions;
